/* ==========================================================================
   通用右侧抽屉开合 —— 2026-08-26 终稿（Figma tPnjQ1EbVgBhInGroWrTN6）
   ==========================================================================
   骨架 snippets/amada-drawer.liquid，样式 assets/amada-drawer.css。

   为什么单独一层：终稿有 10 个同形态抽屉（Size Guide / Dimensions / Material &
   Care / Shipping / Returns / Order Swatches / 快速查看 / 排序 / 购物袋两态）。
   历史经验是每个弹窗各写一遍开合，就会各漏一两个坑（ESC、scroll lock、
   焦点、被 header 盖住）。这里一次写对，业务侧只管内容。

   对外 API（window.AmadaDrawer）：
     open(id)      打开；已开着别的抽屉会先关掉（一次只开一个）
     close(id)     关闭；不传 id 关当前打开的那个
     closeAll()    关掉所有
     isOpen(id)    是否打开
   事件：抽屉根元素上派发 amada:drawer:open / amada:drawer:close（冒泡），
        detail = { id }，业务侧可据此懒加载内容。

   触发器：任意元素 data-amada-drawer-open="<id>"（document 级委托，
   动态插入的 DOM 也能用——分类页卡片是异步渲染的，必须走委托）。
   关闭：data-amada-drawer-close / 遮罩 / ESC。
   ========================================================================== */
(function () {
  'use strict';

  var OPEN_CLASS = 'is-open';
  var LOCK_CLASS = 'amada-drawer-open';
  var TRANSITION_MS = 300; // 与 CSS 的 .3s 保持一致

  /** 当前打开的抽屉 id（一次只开一个） */
  var currentId = null;
  /** 打开前的焦点元素，关闭后还回去（可访问性） */
  var lastFocused = null;
  /** 关闭动画的定时器，避免连点时把刚打开的抽屉 hidden 掉 */
  var hideTimer = null;

  function root(id) {
    if (!id) return null;
    return document.querySelector('[data-amada-drawer="' + cssEscape(id) + '"]');
  }

  /** 属性选择器里 id 可能带引号等字符，做最小转义（不引入 polyfill 依赖） */
  function cssEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  /**
   * 计算面板顶边。
   * 稿里 PDP 抽屉从 header 底（y=102）起、遮罩同起点，header 保持可见可点；
   * 收藏那种从促销条底（y=30）起。header 实际高度会随促销条关闭/吸顶变化，
   * 所以这里**实测** DOM，不硬编码，也不用主题的 --header-height
   * （铁律 8：那个变量的语义没核实过，曾经把布局顶坏）。
   *
   * 🚨 2026-08-27 必须在 lockScroll() **之后**调用（见 open()）。
   * 主题的 header 靠 position:sticky 吸顶，而 scroll lock 给 <html>/<body> 加了
   * overflow:hidden —— sticky 的祖先一旦 overflow:hidden，sticky 立即失效，
   * header 当场掉回文档流、随页面滚出视口。锁之前量到的是「吸顶态」的 bottom=70，
   * 锁之后 header 其实已经在 -700 了，于是抽屉顶上留出 70px 既没 header 也没遮罩的
   * 空档，直接露出商品右栏内容（运营报的「抽屉没全高」就是这个）。
   * 锁之后再量，header 不可见时 bottom 为负 → Math.max 归 0 → 自然变成全屏，
   * 既符合稿「header 可见就贴它底边」的意图，也不会露内容。
   */
  function resolveTop(el) {
    var mode = el.getAttribute('data-amada-drawer-top') || 'header';

    if (mode === 'zero') return 0;

    if (mode === 'promo') {
      var promo = document.querySelector('.announcement-bar');
      if (promo) {
        var pr = promo.getBoundingClientRect();
        return Math.max(0, pr.bottom);
      }
      return 30; // 稿默认促销条 30 高
    }

    // 'header'：量 header 的底边
    var header = document.querySelector('header.header, .header, [data-header]');
    if (header) {
      var hr = header.getBoundingClientRect();
      return Math.max(0, hr.bottom);
    }
    return 0;
  }

  function applyTop(el) {
    // 移动端（≤959）整屏覆盖，顶边恒为 0；CSS 里也写了兜底，这里不要反着覆盖。
    if (window.matchMedia('(max-width: 959px)').matches) {
      el.style.removeProperty('--amada-drawer-top');
      return;
    }
    el.style.setProperty('--amada-drawer-top', resolveTop(el) + 'px');
  }

  function lockScroll() {
    document.documentElement.classList.add(LOCK_CLASS);
  }

  function unlockScroll() {
    document.documentElement.classList.remove(LOCK_CLASS);
  }

  function emit(el, name, id) {
    el.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: { id: id } })
    );
  }

  function open(id) {
    var el = root(id);
    if (!el) return false;
    if (currentId === id) return true;

    // 已开着别的 → 先关（不等动画，避免两个面板同时在场）
    if (currentId) closeImmediate(currentId);

    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }

    lastFocused = document.activeElement;

    el.hidden = false;
    /* 顺序不可换：先锁滚动、再量顶边。
       锁滚动会让主题 sticky header 失效并掉回文档流，量早了会拿到吸顶态的旧值，
       抽屉顶部就会留出一条露出页面内容的空档（详见 resolveTop 的注释）。 */
    lockScroll();
    applyTop(el);

    // 先落一帧再加 is-open，否则 transform 起始态与结束态同帧、动画不触发
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        el.classList.add(OPEN_CLASS);
      });
    });

    el.setAttribute('aria-hidden', 'false');
    currentId = id;

    var closeBtn = el.querySelector('.amada-drawer__close');
    if (closeBtn) closeBtn.focus();

    emit(el, 'amada:drawer:open', id);
    return true;
  }

  /** 不等动画直接收起（切换抽屉时用） */
  function closeImmediate(id) {
    var el = root(id);
    if (!el) return;
    el.classList.remove(OPEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
    el.hidden = true;
    if (currentId === id) currentId = null;
    emit(el, 'amada:drawer:close', id);
  }

  function close(id) {
    var targetId = id || currentId;
    if (!targetId) return;
    var el = root(targetId);
    if (!el) return;

    el.classList.remove(OPEN_CLASS);
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
    currentId = null;

    // 等滑出动画结束再 hidden，否则面板会瞬间消失、看不到退出动画
    hideTimer = window.setTimeout(function () {
      if (!el.classList.contains(OPEN_CLASS)) el.hidden = true;
      hideTimer = null;
    }, TRANSITION_MS);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;

    emit(el, 'amada:drawer:close', targetId);
  }

  function closeAll() {
    var all = document.querySelectorAll('[data-amada-drawer]');
    for (var i = 0; i < all.length; i++) {
      var id = all[i].getAttribute('data-amada-drawer');
      if (all[i].classList.contains(OPEN_CLASS)) close(id);
    }
  }

  function isOpen(id) {
    return id ? currentId === id : Boolean(currentId);
  }

  /* ── 事件委托：触发器与关闭 ───────────────────────────────────────── */
  /* ⚠️ 必须用**捕获阶段**（第三参 true）。
     触发器有的是 <a href="/pages/…">（Size Guide 保留了降级跳转链接），
     而主题自己也给这些锚点绑了 modal 相关的监听。冒泡阶段注册时，
     主题的监听可能先跑并 stopPropagation，我们的 preventDefault 就永远轮不到，
     表现是「点 Size Guide 直接跳走了整页」（验收脚本报
     "Execution context was destroyed, most likely because of a navigation"）。
     捕获阶段从 document 往下走，保证我们第一个拿到事件。 */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest && e.target.closest('[data-amada-drawer-open]');
    if (opener) {
      var id = opener.getAttribute('data-amada-drawer-open');
      if (id && root(id)) {
        e.preventDefault();
        /* 同时掐断后续监听：Size Guide 那个锚点上还留着主题 modal 的
           data-modal-content-id / aria-controls，不掐断的话会连带弹出
           主题的居中弹窗，变成「抽屉 + 弹窗」同时在场。 */
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        open(id);
        return;
      }
    }

    var closer = e.target.closest && e.target.closest('[data-amada-drawer-close]');
    if (closer) {
      var host = closer.closest('[data-amada-drawer]');
      if (host) {
        e.preventDefault();
        close(host.getAttribute('data-amada-drawer'));
      }
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && currentId) close(currentId);
  });

  /* 视口变化时重算顶边：促销条关闭、header 吸顶、横竖屏切换都会改变它。
     铁律 10：带宽度判断的 JS 不能「加载时不符就 return」，这里每次都重算。 */
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (!currentId) return;
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      var el = root(currentId);
      if (el) applyTop(el);
    }, 100);
  });

  /* ------------------------------------------------------------------
     Product Overview 的 learn more 行内展开（2026-09-02 第四轮）
     --------------------------------------------------------------------
     稿 6:11304（收起）/ 4:10906（展开）：点 learn more 就地展开那组规格行，
     再点收起。不再开整屏白页（旧 sheet 弹窗已删）。

     ▍为什么这段代码住在 amada-drawer.js 里
     同一段正文（snippets/amada-pdp-drawer-bodies.liquid）会出现在两个地方：
       · PDP  → sections/amada-pdp-drawers.liquid 渲染的右侧抽屉
       · 分类页 → quick view 把 PDP 的这段 HTML **整段复制**进居中弹窗
     所以处理器必须在**两个页面都已加载**的一份脚本里，而且不能靠 snippet 自带
     <script> —— quick view 是用 innerHTML 塞内容的，innerHTML 插入的 <script> 永远不执行。
     本文件由 layout/theme.liquid:162 全局加载，是唯一同时满足这两条的现成文件；
     选它还有一个原因：**不用为一个模块去改 layout/theme.liquid**（本项目既定铁律）。
     写成 document 级委托，因此内容何时被 innerHTML 插进来都不影响。
     ------------------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-amada-overview-learnmore]') : null;
    if (!btn) return;
    e.preventDefault();

    /* 目标优先按 aria-controls 找（标准契约）；找不到再在同一块 .amada-pdpd-ov
       里就近找 —— quick view 复制过去的那份 id 相同，但万一被运营的富文本破坏，
       就近查找仍能工作。 */
    var id = btn.getAttribute('aria-controls');
    var panel = id ? document.getElementById(id) : null;
    if (!panel) {
      var scope = btn.closest('.amada-pdpd-ov');
      panel = scope ? scope.querySelector('[data-amada-overview-specs]') : null;
    }
    if (!panel) return;

    var willOpen = panel.hidden;
    panel.hidden = !willOpen;
    btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');

    /* 展开后如果内容被顶出可视区，把它滚进来 ——
       quick view 弹窗的可视 body 只有 405px，展开的 9 条规格有 856px，
       不滚的话用户点完「看起来什么都没发生」。
       用最近的可滚动祖先，PDP 抽屉与弹窗共用同一段逻辑。 */
    if (!willOpen) return;
    var scroller = panel.parentElement;
    while (scroller && scroller !== document.body) {
      var oy = window.getComputedStyle(scroller).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && scroller.scrollHeight > scroller.clientHeight) break;
      scroller = scroller.parentElement;
    }
    if (!scroller || scroller === document.body) return;
    var pr = panel.getBoundingClientRect();
    var sr = scroller.getBoundingClientRect();
    if (pr.top < sr.top || pr.top > sr.bottom - 40) {
      scroller.scrollTop += pr.top - sr.top - 20;
    }
  }, true);

  /* ------------------------------------------------------------------
     抽屉正文的「按当前商品过滤」共享规则（2026-09-02 第五轮，运营验收 Q1）
     --------------------------------------------------------------------
     Size Guide 的尺寸图与 Order Swatches 的面料小样都是**店铺级/模板级**配置，
     以前对所有商品无差别显示 —— 运营看到灰/蓝沙发点开是「PERPETUA WHITE / SEAGLASS」，
     尺寸图是通用三张，于是问「这是什么意思」。

     规则住在这里而不是 amada-pdp.js：同一份正文会出现在 PDP 抽屉与 quick view 弹窗
     两处（quick view 是把某一个 PDP 页的 HTML 整段复制过来的），
     处理器必须在两个页面都已加载的一份脚本里。理由与上面 learn more 那段相同。
     本模块只做**纯 DOM 规则**，「当前选中的型号/颜色是什么」由调用方各自取 ——
     PDP 与 quick view 的选择器天差地别，但规则只有这一份。
     ------------------------------------------------------------------ */
  function norm(v) {
    return String(v == null ? '' : v).trim().toLowerCase();
  }

  /** 双向包含：填 Grey 能命中 Light Grey，填 Light Grey 也能命中 Grey */
  function looseMatch(needleList, value) {
    var v = norm(value);
    if (!v) return false;
    var parts = String(needleList || '').split(',');
    for (var i = 0; i < parts.length; i++) {
      var n = norm(parts[i]);
      if (!n) continue;
      if (v.indexOf(n) !== -1 || n.indexOf(v) !== -1) return true;
    }
    return false;
  }

  /**
   * Size Guide 按型号过滤。
   * @param {Element} scope 抽屉或弹窗根节点
   * @param {String}  shape 当前选中的型号/布局值（调用方取）
   * @param {Number}  layoutCount 当前型号下有几种布局款式（整组都没配 group 时的兜底口径）
   */
  function syncSizeGuide(scope, shape, layoutCount) {
    var host = scope || document;
    var items = host.querySelectorAll('[data-size-guide-group]');
    if (!items.length) return 0;
    var shown = 0;
    var anyGrouped = false;
    for (var i = 0; i < items.length; i++) {
      if (norm(items[i].getAttribute('data-size-guide-group'))) { anyGrouped = true; break; }
    }
    for (var j = 0; j < items.length; j++) {
      var g = items[j].getAttribute('data-size-guide-group');
      /* 留空 = 通用，始终显示；否则按「型号值包含关键词」匹配 */
      var on = !norm(g) || looseMatch(g, shape);
      items[j].hidden = !on;
      if (on) shown++;
    }
    /* 兜底分两种情况，别混成一条：
       ① **整组都没配 group**（preset 默认内容，运营还没填）——
          沿用 2026-08-28 运营验收定下的口径：格子数跟着当前型号下的布局款式数走。
          运营原话「U-Shaped 只有一种类型，就只展示一种类型的图片和参数」；
          🚨 这里**不能**改回「全部显示」，那正是 08-28 被反馈的问题。
       ② **配了 group 但当前型号一个都没命中** —— 说明这个型号还没配尺寸图。
          此时显示全部：空抽屉比多余的图更糟，而且「配了却没命中」多半是关键词写错，
          全显示能让运营立刻看出来。已写进报告让运营知道这是提示信号而不是正常态。 */
    if (!anyGrouped) {
      var n = layoutCount;
      if (!(n >= 1)) n = 1;
      if (n > items.length) n = items.length;
      for (var q = 0; q < items.length; q++) items[q].hidden = q >= n;
      applySingleMode(items);
      return n;
    }
    if (shown === 0) {
      for (var r = 0; r < items.length; r++) items[r].hidden = false;
      applySingleMode(items);
      return items.length;
    }
    applySingleMode(items);
    return shown;
  }

  /**
   * Dimensions 按空值标题行分组过滤，型号名先压缩连续空白再精确匹配。
   * 不用 looseMatch：双向包含会让 Ottoman 命中所有带 + Ottoman 的型号。
   * 无标题命中时退回全部行和总图，保证其它商品保持原有展示。
   */
  function syncDimensions(scope, shape) {
    var host = scope || document;
    var dimensions = host.querySelectorAll('.amada-pdpd-dim');
    var selected = norm(shape).replace(/\s+/g, ' ');
    for (var i = 0; i < dimensions.length; i++) {
      var panel = dimensions[i];
      var headings = panel.querySelectorAll('[data-dim-heading][data-dim-group]');
      var matched = false;
      for (var j = 0; j < headings.length; j++) {
        var headingGroup = norm(headings[j].getAttribute('data-dim-group')).replace(/\s+/g, ' ');
        if (headingGroup === selected) { matched = true; break; }
      }
      var rows = panel.querySelectorAll('[data-dim-group]');
      for (var k = 0; k < rows.length; k++) {
        var group = norm(rows[k].getAttribute('data-dim-group')).replace(/\s+/g, ' ');
        rows[k].hidden = matched && !!group && group !== selected;
      }
      var diagrams = panel.querySelectorAll('[data-dim-diagram]');
      var hasDiagram = false;
      for (var d = 0; d < diagrams.length; d++) {
        var diagramGroup = norm(diagrams[d].getAttribute('data-dim-diagram')).replace(/\s+/g, ' ');
        var showDiagram = matched && diagramGroup === selected;
        diagrams[d].hidden = !showDiagram;
        if (showDiagram) hasDiagram = true;
      }
      var defaults = panel.querySelectorAll('[data-dim-diagram-default]');
      for (var f = 0; f < defaults.length; f++) defaults[f].hidden = hasDiagram;
    }
  }

  /* ------------------------------------------------------------------
     Size Guide 单图态（2026-09-14 运营 #5）
     --------------------------------------------------------------------
     可见格数 == 1 时，稿里那套「3 列 × 196 宽缩略图」就没有意义了 ——
     运营看到的是一张被 aspect-ratio:188/240 + object-fit:cover **裁掉**的尺寸图
     （实测原图 1440×1440，画出来只有 188×240，标注全被切光）。
     所以可见 1 张时给网格加 .is-single，由 CSS 换成单列 + 原比例铺满内容宽，
     同时把 src 升级到 data-full-src 的大图（先留着缩略图，大图 onload 再换，
     铁律 33：替换前必须确认这张还是当前要显示的那张）。

     为什么写在 syncSizeGuide 里而不是各调用方：PDP（assets/amada-pdp.js）和
     quick view（assets/amada-quickview.js）都只调这一个入口，写在这里两端同时生效，
     一行调用方代码都不用动（quick view 的 JS 本轮由别的组在改，更不能碰）。
     ------------------------------------------------------------------ */
  function applySingleMode(items) {
    var grids = [];
    var visible = [];
    for (var i = 0; i < items.length; i++) {
      var g = items[i].parentElement;
      if (g && grids.indexOf(g) === -1) grids.push(g);
      if (!items[i].hidden) visible.push(items[i]);
    }
    var single = visible.length === 1;
    for (var k = 0; k < grids.length; k++) {
      grids[k].classList.toggle('is-single', single);
    }
    /* 退出单图态时把升级过的图还原回缩略图 —— 不还原的话 196 的格子里
       挂着 1600 的大图，多图态白白多下几 MB。
       🚨 「要升级的那一张」必须跳过还原：还原会把 src 写回缩略图，
       而 img.currentSrc 在同一帧里还来不及更新，紧接着的 upgradeToFull 就会
       误判成「已经是大图了」直接返回 —— 表现是随机有几次单图态还是糊的
       （960 宽实测复现过，1440 下没复现，典型的时序假阴性）。 */
    var keep = single ? visible[0] : null;
    for (var m = 0; m < items.length; m++) {
      if (items[m] !== keep) restoreThumb(items[m]);
    }
    if (single) upgradeToFull(keep);
  }

  /** 抽屉/弹窗打开后补跑一次：首屏那次 sync 时面板还是 hidden，大图不该提前下载 */
  function upgradeVisibleSingles(scope) {
    var grids = (scope || document).querySelectorAll('.amada-pdpd-sizeguide__grid.is-single');
    for (var i = 0; i < grids.length; i++) {
      var items = grids[i].querySelectorAll('[data-sizeguide-zoom]');
      for (var j = 0; j < items.length; j++) {
        if (!items[j].hidden) { upgradeToFull(items[j]); break; }
      }
    }
  }

  document.addEventListener('amada:drawer:open', function (e) {
    upgradeVisibleSingles(e.target);
  });
  document.addEventListener('amada:modal:open', function (e) {
    upgradeVisibleSingles(e.target);
  });

  function restoreThumb(item) {
    var img = item.querySelector('.amada-pdpd-sizeguide__thumb');
    if (!img) return;
    var thumb = img.getAttribute('data-thumb-src');
    if (!thumb) return;
    img.removeAttribute('data-thumb-src');
    if (img.getAttribute('data-thumb-srcset')) {
      img.setAttribute('srcset', img.getAttribute('data-thumb-srcset'));
      img.removeAttribute('data-thumb-srcset');
    }
    img.src = thumb;
  }

  function upgradeToFull(item) {
    if (!item) return;
    var img = item.querySelector('.amada-pdpd-sizeguide__thumb');
    var full = item.getAttribute('data-full-src');
    if (!img || !full) return;
    /* 面板还没打开（hidden / display:none）时不要预载 ——
       首屏的 syncSizeGuide 在 DOMContentLoaded 就跑了，那时抽屉是关的，
       在这里下 1600 宽的大图等于给每个 PDP 首屏白加几百 KB。
       抽屉/弹窗真的打开时由 upgradeVisibleSingles 补跑。 */
    if (!item.offsetParent && getComputedStyle(item).position !== 'fixed') return;
    /* data-thumb-src 存在 = 这张已经升级过了，直接收工。
       用属性而不是 currentSrc 做「升级与否」的判据：属性是我们自己写的、同步可靠，
       currentSrc 由浏览器异步更新，刚改完 src 的那一帧读到的还是旧值。 */
    if (img.hasAttribute('data-thumb-src')) return;
    /* data-full-src 是协议相对的 `//cdn…`，而 img.src 属性读出来是绝对 URL，
       直接比字符串永远不相等 → 会在多图/单图之间来回空转。先归一化再比。 */
    full = new URL(full, window.location.href).href;
    var current = img.getAttribute('src');
    if (!current) return;
    current = new URL(current, window.location.href).href;
    if (current === full && !img.getAttribute('srcset')) return;
    var pre = new Image();
    pre.onload = function () {
      /* 竞态守卫：预载期间用户可能已经切了型号 / 回到多图态。 */
      if (item.hidden || !item.parentElement || !item.parentElement.classList.contains('is-single')) return;
      if (img.hasAttribute('data-thumb-src')) return;
      img.setAttribute('data-thumb-src', img.currentSrc || current);
      /* srcset 的权重高于 src，不摘掉的话浏览器会继续用 196px 那档，
         「换了 src 却还是糊的」——这是同类改动最常见的失手点。 */
      if (img.getAttribute('srcset')) {
        img.setAttribute('data-thumb-srcset', img.getAttribute('srcset'));
        img.removeAttribute('srcset');
      }
      img.src = full;
    };
    pre.src = full;
  }

  /* 多图态点击某一格 → 打开通用灯箱（window.AmadaLightbox，定义在 assets/amada-reviews.js，
     PDP 与 quick view 都已加载它）。单图态本身就是大图，点了不再开灯箱。
     委托挂在 document 上：抽屉正文可能被 quick view 用 innerHTML 整段换掉。 */
  document.addEventListener('click', function (e) {
    var item = e.target && e.target.closest ? e.target.closest('[data-sizeguide-zoom]') : null;
    if (!item) return;
    var grid = item.parentElement;
    if (grid && grid.classList.contains('is-single')) return;
    if (!window.AmadaLightbox) return;
    e.preventDefault();

    var all = grid ? [].slice.call(grid.querySelectorAll('[data-sizeguide-zoom]')) : [item];
    var vis = all.filter(function (n) { return !n.hidden; });
    var items = vis.map(function (n) {
      var im = n.querySelector('.amada-pdpd-sizeguide__thumb');
      return {
        thumb: im ? (im.getAttribute('data-thumb-src') || im.currentSrc || im.src) : '',
        full: n.getAttribute('data-full-src') || (im ? im.src : ''),
      };
    });
    window.AmadaLightbox.open(items, vis.indexOf(item), item);
  });

  /**
   * Order Swatches 按颜色过滤。命中 0 张时改显示该商品自己的色卡。
   * @param {Element} scope 抽屉或弹窗根节点
   * @param {String}  color 当前选中的颜色值名
   * @param {String}  image 当前颜色的色卡图 URL（取不到就只显示值名）
   */
  function syncSwatches(scope, color, image) {
    var host = scope || document;
    var cards = host.querySelectorAll('[data-pdpd-swatch][data-swatch-color-match]');
    if (!cards.length) return 0;
    var shown = 0;
    for (var i = 0; i < cards.length; i++) {
      var m = cards[i].getAttribute('data-swatch-color-match');
      /* 留空 = 不限颜色（老数据），仍然显示 —— 运营补完字段后才会真正收窄 */
      var on = !norm(m) || looseMatch(m, color);
      cards[i].hidden = !on;
      if (on) shown++;
    }

    var native = host.querySelector('[data-pdpd-swatch-native]');
    if (native) {
      var useNative = shown === 0 && !!norm(color);
      native.hidden = !useNative;
      if (useNative) {
        var nm = native.querySelector('[data-pdpd-swatch-native-name]');
        var th = native.querySelector('[data-pdpd-swatch-native-thumb]');
        var ds = native.querySelector('[data-pdpd-swatch-native-desc]');
        if (nm) nm.textContent = color;
        if (ds) ds.textContent = 'Current selection';
        if (th) th.style.backgroundImage = image ? 'url("' + image + '")' : '';
      }
    }
    return shown;
  }

  window.AmadaDrawerContent = {
    syncSizeGuide: syncSizeGuide,
    syncDimensions: syncDimensions,
    syncSwatches: syncSwatches,
    looseMatch: looseMatch
  };

  window.AmadaDrawer = {
    open: open,
    close: close,
    closeAll: closeAll,
    isOpen: isOpen
  };

  /* ── 补开脚本就绪前的点击（2026-10-15）───────────────────────────
     layout/theme.liquid 的 head 里有一段早点击排队，把本脚本就绪前点到的触发器
     记在 window.__amadaDrawerPending。等 DOMContentLoaded 再开：那时所有 defer
     脚本都跑完了，各业务侧的 amada:drawer:open 内容同步监听也已绑好。
     抽屉不存在且触发器是链接时按原 href 跳转，保留 Size Guide 的降级行为。 */
  function replayPending() {
    var opener = window.__amadaDrawerPending;
    if (!opener) return;
    window.__amadaDrawerPending = null;
    var id = opener.getAttribute('data-amada-drawer-open');
    if (id && root(id)) {
      open(id);
    } else if (opener.href) {
      window.location.href = opener.href;
    }
  }
  document.addEventListener('DOMContentLoaded', replayPending);
  window.addEventListener('load', replayPending);
})();
